import React, { Component } from 'react';
import { arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import Card from 'ps-components/Card/Card';
import CardLayover from '../CardLayover/CardLayover';
import CardLoading from '../CardLoading/CardLoading';
import DeleteConfirmation from '../DeleteConfirmation/DeleteConfirmation';
import RangeChart from 'ps-components/RangeChart/RangeChart';
import SeeMore from 'ps-components/Modules/AddJob/SeeMore';
import SurveyProfileAnalyzed from 'ps-components/Modules/AddJob/SurveyProfileAnalyzed';

import { formatCurrencyIntl } from 'ps-components/lib/formatters';
import './CrowdSourcedCard.scss';

const CURRENT_MATCH_HEADER_TEXT = 'Your Current Match';
const DELETE_CONFIRMATION_DISCLOSURE = 'All changes for compensible factors will also be removed.';
const DELETE_CONFIRMATION_TEXT = 'Are you sure you want to remove this match from pricing?';
const DELETE_MATCH_TEXT = 'Remove Match';
const NO_PAY_RANGE_TEXT = 'No salary data available';
const SEARCH_MATCH_TEXT = 'Change Match';

class CrowdSourcedCard extends Component {
  static propTypes = {
    crowdSourcedCut: object,
    deleteMatch: func,
    isLoading: bool,
    isMP: bool,
    isSlideoutOpen: bool,
    jobDescription: string,
    jobDetails: shape({
      details: shape({
        payscale_job_info: shape({
          saveProfilesAnalyzed: string
        })
      }),
      jobIdList: arrayOf(number)
    }),
    orgJobCurrency: string,
    rangeChartData: shape({
      highVariance: bool,
      matchId: string,
      payRange: arrayOf(number),
      payRangeMax: number,
      payRangeMin: number
    }),
    saveProfilesAnalyzed: func,
    searchForNewMatch: func,
    selectedJobTitle: string
  };

  constructor(props) {
    super(props);

    this.state = { showDeleteConfirmation: false };

    this.bottomLabelFormatter = this.bottomLabelFormatter.bind(this);
    this.deleteMatch = this.deleteMatch.bind(this);
    this.renderCardDetails = this.renderCardDetails.bind(this);
    this.renderDeleteConfirmation = this.renderDeleteConfirmation.bind(this);
    this.renderLoadingPlaceholder = this.renderLoadingPlaceholder.bind(this);
    this.toggleDeleteConfirmation = this.toggleDeleteConfirmation.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { showDeleteConfirmation } = this.state;
    const { isSlideoutOpen } = this.props;

    if (isSlideoutOpen && !prevProps.isSlideoutOpen && showDeleteConfirmation) {
      this.toggleDeleteConfirmation();
    }
  }

  bottomLabelFormatter(val, index) {
    const { orgJobCurrency } = this.props;
    const intl = {
      currency: orgJobCurrency === 'USD' ? '$' : 'C$',
      display: 'symbol',
      preferHourlyRate: false,
      useHourly: false,
      useThousands: true
    };

    return formatCurrencyIntl(val, intl, window.app);
  }

  deleteMatch() {
    const { deleteMatch } = this.props;

    this.toggleDeleteConfirmation();
    deleteMatch(true);
  }

  renderCardDetails() {
    const {
      deleteMatch,
      isMP,
      jobDescription,
      rangeChartData,
      saveProfilesAnalyzed,
      searchForNewMatch,
      selectedJobTitle
    } = this.props;
    const hasPayRange = rangeChartData?.payRange?.length > 0;

    return (
      <React.Fragment>
        <section className="card__row current-match-card__header-container">
          <div className="current-match-card__job-title-container">
            <div className="current-match-card__job-title-note">{CURRENT_MATCH_HEADER_TEXT}</div>
            <h3 className="current-match-card__job-title">{selectedJobTitle}</h3>
          </div>
          <div className="current-match-card__details-container">
            <img className="current-match-card__logo" src="/view/static/mp/images/payScale-logo.png" />
            <SurveyProfileAnalyzed
              disabled={false}
              dropdownOnJobMatchCard={true}
              isMP={isMP}
              jobDetails={this.props.jobDetails}
              saveProfilesAnalyzed={saveProfilesAnalyzed}
              surveyRow={this.props.crowdSourcedCut}
            />
          </div>
        </section>
        <div className="card__row current-match-card__details">
          <div className="current-match-card__tasks-text">
            <SeeMore maxChar={300}>{jobDescription}</SeeMore>
          </div>
        </div>
        <div className="card__row current-match-card__pay">
          <div className="current-match-card__pay-chart">
            {hasPayRange && (
              <RangeChart
                barHeight={20}
                bottomLabelFormatter={this.bottomLabelFormatter}
                className="match-range"
                colors={['#22d6dd', '#a7eff1']}
                data={rangeChartData?.payRange}
                hasLine={!rangeChartData?.highVariance}
                height={25}
                hideTopLabels
                id={`match${rangeChartData?.matchId}-market-range-crowd-sourced-card`}
                invertLabels={false}
                labelFontSize={11}
                labelTextAnchor={['start', 'middle', 'end']}
                labelToBarSpacingTop={5}
                labelsVisible={true}
                scaleMax={rangeChartData?.payRangeMax}
                scaleMin={rangeChartData?.payRangeMin}
                width={420}
              />
            )}
            {(!rangeChartData?.payRange || rangeChartData?.payRange?.length === 0) && (
              <div className="current-match-card__no-data">
                <i className="icon-warning" />
                {NO_PAY_RANGE_TEXT}
              </div>
            )}
          </div>
          <div className="current-match-card__pay-actions">
            <a className="current-match-card__pay-action" onClick={searchForNewMatch}>
              {SEARCH_MATCH_TEXT}
            </a>
            <a className="current-match-card__pay-action" onClick={this.toggleDeleteConfirmation}>
              {DELETE_MATCH_TEXT}
            </a>
          </div>
        </div>
      </React.Fragment>
    );
  }

  renderDeleteConfirmation() {
    return (
      <DeleteConfirmation
        buttonText={DELETE_MATCH_TEXT}
        confirmationDisclosure={DELETE_CONFIRMATION_DISCLOSURE}
        confirmationText={DELETE_CONFIRMATION_TEXT}
        onCancel={this.toggleDeleteConfirmation}
        onRemove={this.deleteMatch}
      />
    );
  }

  renderLoadingPlaceholder() {
    return <CardLoading loadingMessage="Loading crowd-sourced match..." />;
  }

  toggleDeleteConfirmation() {
    const { showDeleteConfirmation } = this.state;
    this.setState({ showDeleteConfirmation: !showDeleteConfirmation });
  }

  render() {
    const { showDeleteConfirmation } = this.state;
    const { isLoading } = this.props;

    return (
      <Card className="current-match-card" raiseOnHover={false}>
        <CardLayover
          display={showDeleteConfirmation || isLoading}
          layoverContent={isLoading ? this.renderLoadingPlaceholder : this.renderDeleteConfirmation}
        >
          <div className="crowd-sourced-match-card__content">{this.renderCardDetails()}</div>
        </CardLayover>
      </Card>
    );
  }
}

export default CrowdSourcedCard;
